import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Label,
  Input,
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
} from "reactstrap";
import Select from "react-select";
import { withTranslation } from "react-i18next";
import { format } from 'date-fns';

function getTimeZoneOffsetLabel() {
  const offsetMinutes = new Date().getTimezoneOffset();
  const sign = offsetMinutes <= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, "0");
  const mins = String(abs % 60).padStart(2, "0");
  return `目前時區：UTC${sign}${hours}:${mins}`;
}

function getLocalDayBoundaryString() {
  const offsetMinutes = new Date().getTimezoneOffset(); // minutes *behind* UTC
  const localHour = (24 - offsetMinutes / 60) % 24; // UTC+X → local boundary = X AM

  const hour = Math.floor(localHour);
  const suffix = hour < 12 ? "上午" : "下午";
  const hourIn12 = hour % 12 === 0 ? 12 : hour % 12;

  return `過去一天（以 UTC+0 計日，本地時間${suffix} ${hourIn12} 點跨日）`;
}

const PointRedemptionRuleModal = ({ 
  t, 
  showColorHint,
  setShowColorHint,
  maxPointsData,
  isOpen, 
  toggle, 
  redemptionRuleModalStyles
}) => {

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ minWidth: '50%' }}>
      <ModalHeader toggle={toggle}>{t("兌換規則說明")}</ModalHeader>
      <ModalBody>
        {/* Placeholder content */}
        <p>{`積分兌換的詳細規則：在每天（按照+0時區計算）之內，系統會記錄當天此帳號達到最高點數總額及達到的時間。
          達到該點數總額的時間點後，進行任何「兌換」並不會影響到當天可兌換的積分數量。`}</p>
        <p>當天總共可兌換的積分總數量是（「尚未申請兌換的點數總額」+「當天內已申請兌換的點數總額」）達到最大值時的 1/3。</p>

        <hr style={{ borderTop: '1px solid grey' }}/>
        <Row>
          <Col xs={6}><p>例如...</p></Col>
          <Col xs={6} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", paddingBottom: "5px", paddingRight: "16px"}}>
            <Input
              id={`show-color-hint`}
              className="me-2 attribute-checkbox"
              type="checkbox"
              checked={showColorHint}
              onChange={() => {setShowColorHint(!showColorHint)}}
              style={{ outline: "none", boxShadow: "none" }}
            />
            <Label
              htmlFor={`show-color-hint`}
              className="attribute-label"
              style={{ margin: "0" }}
            >
              顯示顏色提示
            </Label>
          </Col>
        </Row>

        <div style={{ fontSize: '12px' }}>
        <p>{`(1) 小明在當天 12 點因為填答獲得點數，點數來到當天到這時為止最高的 4,500 點，且當天已申請兌換 0 點。
        同一天 14 點時，小明申請兌換 500 點。申請完成，該筆兌換進入處理程序。
        此時先計算小明（「尚未申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'red' }}>4,000</span>
        {` 點」 +「當天內已申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'blue' }}>500</span>
        {` 點」）x (1/3) = 1,500 點。這次申請兌換了 500 點，所以這時候最高還可以申請兌換 1,500-500=1,000 點。`}</p>
        <p>{`(2) 小明這時候沒有繼續申請兌換，而是在同一天 14 到 16 點間，使用了兌換以外的方式，在平台上花掉了 300 點，尚未申請兌換的點數總額變為 `}
        <span style={{ color: !showColorHint ? '#495057' : 'red' }}>4,000</span>
        {`-300=3,700 點。
        此時先計算小明（「尚未申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'darkorange' }}>3700</span>
        {` 點」 +「當天內已申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'blue' }}>500</span>
        {` 點」）x (1/3) = 1,400 點。前面小明有申請兌換 500 點了，所以小明 16 點此時還可以申請兌換 1,400-500=900 點。`}</p>
        
        <hr style={{ borderTop: '1px dashed grey', marginLeft: '30%', marginRight: '30%' }}/>

        <p>{`(3) 小明這時候還是沒有繼續申請兌換，而是在同一天 16 到 22 點間，進行問題填答，在平台上賺到了 900 點，尚未申請兌換的點數總額變為 `}
        <span style={{ color: !showColorHint ? '#495057' : 'darkorange' }}>3700</span>
        {`+900=4,600 點。
        此時先計算小明（「尚未申請兌換的點數總額 4,600 點」 +「當天內已申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'blue' }}>500</span>
        {` 點」）x (1/3) = 1,700 點。前面小明有申請兌換 500 點了，所以小明 22 點此時還可以申請兌換 1,700-500=1,200 點。`}</p>
        <p>{`(4) 小明 22 點時，申請兌換 `}
        <span style={{ color: !showColorHint ? '#495057' : 'deeppink' }}>800</span>
        {` 點。申請完成，這筆兌換進入處理程序，尚未申請兌換的點數總額變成 3,800 點。
        現在小明（「尚未申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'purple' }}>3800</span>
        {` 點」 +「當天內已申請兌換的點數總額 1,300 點」）x (1/3) = 1,700 點。前面已經申請兌換了 1,300 ( `}
        <span style={{ color: !showColorHint ? '#495057' : 'blue' }}>500</span>
        {`+`}
        <span style={{ color: !showColorHint ? '#495057' : 'deeppink' }}>800</span>
        {` ) 點，所以這時候最高還可以申請兌換 1,700-1,300=400 點。`}</p>
        
        <hr style={{ borderTop: '1px dashed grey', marginLeft: '30%', marginRight: '30%' }}/>

        <p>{`(5) 小明這時候沒有繼續申請兌換，也沒有任何點數變動，來到了隔天的凌晨一點。
        隔天凌晨一點的這時候，已經到了新的一天，所以「當天內已申請兌換的點數總額」變成 0 點。
        這時候，小明（「尚未申請兌換的點數總額 `}
        <span style={{ color: !showColorHint ? '#495057' : 'purple' }}>3800</span>
        {` 點」 +「當天內已申請兌換的點數總額 0 點」）x (1/3) = 1,267 點。這天還沒有進行任何兌換申請，所以這時候最高還可以申請兌換 1,267 點。`}</p>
        </div>


        <hr style={{ borderTop: '1px solid grey' }}/>

        {/* 當日最高點數總額組成 */}
        <p className="mb-2" style={{ fontSize: "18px"}}>當日最高點數總額組成</p>
        <p className="mb-1">{getLocalDayBoundaryString()}內到達最高點數的時間：</p>
        <p className="mb-1">
          {maxPointsData?.lastUpdated
            ? format(new Date(maxPointsData.lastUpdated), 'yyyy-MM-dd HH:mm:ss')
            : '（無紀錄）'}
        </p>
        <p className="mb-1">尚未申請兌換的點數總額：{maxPointsData.confirmedPoints}</p>
        <p className="mb-1">當天內已申請兌換的點數總額：{maxPointsData.pendingRedemptionPoints}</p>
        <p className="mb-1">最高可兌換點數 = {`(${maxPointsData.confirmedPoints}+${maxPointsData.pendingRedemptionPoints}) x (1/3) = ${Math.ceil((maxPointsData.confirmedPoints+maxPointsData.pendingRedemptionPoints)/3)}`}</p>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={toggle}>
          {t("關閉")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withTranslation()(PointRedemptionRuleModal);
